import React, { useEffect, useRef } from "react";
import styled from "@emotion/styled";
import Image from "@components/item/Image";
import { useTranslation } from "react-i18next";
import { useTheme } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import ProductLikedButton from "@components/button/ProductLikedButton";
import { toPrice } from "@utils/functions";

const Container = styled.div({
  minWidth: "14rem",
  display: "flex",
  flexDirection: "column",
  height: "100%",
  cursor: "pointer",
});
const ImageWrapper = styled.div({
  position: "relative",
  width: "100%",

  objectFit: "cover",

  "::after": {
    content: `""`,
    display: "block",
    paddingBottom: "100%",
  },
});

const ImageItem = styled.div({
  width: "100%",
  height: "100%",

  position: "absolute",
});

const DescWrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  width: "100%",
  gap: "1.6rem",
  padding: "1rem 0.8rem",
});

const ProductInfoWrapper = styled.div({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "0.8rem",
});

const PriceWrapper = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "0.8rem",
});

const Brand = styled.h3(({ theme }) => ({
  color: theme.color.black,
  fontSize: "1.6rem",
  width: "16rem",
  fontWeight: theme.fontWeight.semiBold,
}));

const Desc = styled.p(({ theme }) => ({
  color: theme.color.gray,
  fontSize: "1.4rem",
  lineHeight: "2rem",
  fontWeight: theme.fontWeight.regular,
}));

const Price = styled.span(({ theme }) => ({
  fontWeight: 700,
  color: theme.color.green,
}));

const MemberPrice = styled.span(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  fontSize: "1rem",
  lineHeight: "1rem",
  fontWeight: 400,
  color: theme.color.green,
  borderRadius: "0.5rem",
  padding: "0.5rem",
  backgroundColor: theme.color.light_green,
}));

const Loves = styled.div(({ theme }) => ({
  display: "flex",
  gap: "2.4rem",
  color: theme.color.gray,
  fontSize: "1.2rem",
  fontWeight: theme.fontWeight.medium,
  lineHeight: "1.2rem",
}));

const LovesItem = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "0.4rem",
}));

const SoldoutLayout = styled.div(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: theme.color.black,
  opacity: 0.5,
}));

const SoldoutText = styled.div(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  fontWeight: theme.fontWeight.semiBold,
  lineHeight: "1.6rem",
  color: theme.color.white,
  transform: "translate(-50%, -50%)",
  whiteSpace: "pre",
}));

type Props = {
  brand: string;
  name: string;
  src: string;
  price: number;
  haveLike?: boolean;
  likeCount: number;
  chatCount: number;
  soldout: boolean;
  id: number;
  isLiked: boolean;
  condition: string;
  isActive?: boolean;
};

const ProductCard = ({
  brand,
  name,
  src,
  price,
  haveLike,
  likeCount,
  chatCount,
  soldout,
  id,
  isLiked,
  condition,
  isActive,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const lang = window.localStorage.getItem("language");
  const scrollPositionRef = useRef<HTMLDivElement>(null);

  const handleNavigateDetailPage = (id: number | string) => {
    const scrollPosition = window.scrollY.toString() || "0";

    sessionStorage.setItem(
      "productListScrollPosition",
      scrollPosition.toString() || "0",
    );

    navigate(`/product-detail/${id}`);
  };

  return (
    <Container
      onClick={(e) => handleNavigateDetailPage(id)}
      ref={scrollPositionRef}>
      <ImageWrapper>
        <ImageItem>
          <Image src={src} width="100%" height="100%" />
        </ImageItem>

        {/* 품절 상품 표시*/}
        {soldout && (
          <>
            <SoldoutLayout></SoldoutLayout>
            <SoldoutText>{t("품절")}</SoldoutText>
          </>
        )}

        {/* 비활성화 상품 표시*/}
        {isActive && (
          <>
            <SoldoutLayout></SoldoutLayout>
            <SoldoutText>{t("비활성화된 상품")}</SoldoutText>
          </>
        )}
      </ImageWrapper>
      <DescWrapper>
        <ProductInfoWrapper>
          <Brand className="ellipsis-1">{brand}</Brand>
          <Desc className="ellipsis-1">{name}</Desc>
        </ProductInfoWrapper>

        <PriceWrapper>
          <Price>
            {condition === "NEW"
              ? toPrice(price)
              : toPrice(Math.round(price * 2))}
          </Price>

          <MemberPrice>{t("회원가")}</MemberPrice>
        </PriceWrapper>

        {haveLike && (
          <Loves>
            <LovesItem>
              <ProductLikedButton isLiked={isLiked} productId={id}>
                <Image
                  src={
                    isLiked
                      ? theme.image.like_full.default
                      : theme.image.love.default
                  }
                  width="2rem"
                  height="2rem"
                />
              </ProductLikedButton>

              <span>
                {likeCount >= 9999 ? "9,999+" : likeCount.toLocaleString()}
              </span>
            </LovesItem>

            <LovesItem>
              <Image
                src={theme.image.chat.default}
                width="2rem"
                height="2rem"
              />
              <span>{chatCount}</span>
            </LovesItem>
          </Loves>
        )}
      </DescWrapper>
    </Container>
  );
};

export default ProductCard;
